import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useColorModeValue,
  useToast,
  useDisclosure,
  Spinner,
  HStack,
  Select,
} from '@chakra-ui/react';
import { ViewIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import Card from 'components/card/Card';
import { getAllAccountsSummary, getAccountByStudent } from 'services/currentAccount.service';
import AccountDetailModal from 'components/currentAccount/AccountDetailModal';
import { useSearch } from 'contexts/SearchContext';
import FormattedDocument from 'components/common/FormattedDocument';

const DEFAULT_LIMIT = 20;

export default function CurrentAccount() {
  const [searchParams] = useSearchParams();
  const { searchQuery, updateSearchQuery } = useSearch();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [activeCount, setActiveCount] = useState(0);
  const [totalBalance, setTotalBalance] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const bgCard = useColorModeValue('white', 'navy.800');

  const updateSearchQueryRef = useRef(updateSearchQuery);
  updateSearchQueryRef.current = updateSearchQuery;

  useEffect(() => {
    return () => updateSearchQueryRef.current('');
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const latestRequestRef = useRef(0);

  const loadAccounts = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      const search = (searchQuery || '').trim();
      const response = await getAllAccountsSummary({
        page,
        limit,
        ...(search ? { search } : {}),
      });
      if (requestId !== latestRequestRef.current) return; // respuesta vieja, ignorar
      if (response.status === 200) {
        const payload = response.payload || {};
        setAccounts(payload.accounts || []);
        setTotal(payload.total || 0);
        setTotalPages(payload.totalPages || 1);
        setActiveCount(payload.activeCount || 0);
        setTotalBalance(payload.totalBalance || 0);
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las cuentas',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  }, [page, limit, searchQuery, toast]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    const studentId = searchParams.get('studentId');
    if (!studentId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getAccountByStudent(studentId);
        const account = response?.payload?.account;
        if (!cancelled && account) {
          setSelectedAccount(account);
          onOpen();
        }
      } catch (error) {
        if (!cancelled) {
          toast({
            title: 'No se pudo abrir la cuenta corriente',
            description: error?.message,
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, onOpen, toast]);

  const handleViewAccount = (account) => {
    setSelectedAccount(account);
    onOpen();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-UY', {
      style: 'currency',
      currency: 'UYU',
    }).format(amount || 0);
  };

  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const hasSearch = Boolean((searchQuery || '').trim());

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card bg={bgCard} p="20px">
        <Flex gap="20px" mb="20px" flexWrap="wrap">
          <Box bg="blue.50" p="15px" borderRadius="10px" flex="1" minW="200px">
            <Text fontSize="sm" color="gray.600" mb="5px">
              Total Cuentas
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="blue.600">
              {total}
            </Text>
          </Box>
          <Box bg="green.50" p="15px" borderRadius="10px" flex="1" minW="200px">
            <Text fontSize="sm" color="gray.600" mb="5px">
              Cuentas Activas
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="green.600">
              {activeCount}
            </Text>
          </Box>
          <Box bg="red.50" p="15px" borderRadius="10px" flex="1" minW="200px">
            <Text fontSize="sm" color="gray.600" mb="5px">
              Total Deuda
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="red.600">
              {formatCurrency(totalBalance)}
            </Text>
          </Box>
        </Flex>

        <Box overflowX="auto" position="relative" minH="200px">
          {loading && (
            <Flex
              position="absolute"
              inset={0}
              align="center"
              justify="center"
              bg="blackAlpha.50"
              zIndex={1}
            >
              <Spinner />
            </Flex>
          )}
          <Table variant="simple" color={textColor}>
            <Thead>
              <Tr>
                <Th borderColor={borderColor}>Nombre</Th>
                <Th borderColor={borderColor}>Documento</Th>
                <Th borderColor={borderColor} isNumeric>
                  Saldo
                </Th>
                <Th borderColor={borderColor} isNumeric>
                  Débito
                </Th>
                <Th borderColor={borderColor}>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {!loading && accounts.length === 0 ? (
                <Tr>
                  <Td colSpan={5} textAlign="center" py="40px">
                    <Text color="gray.500">
                      {hasSearch
                        ? 'No se encontraron cuentas'
                        : 'No hay cuentas registradas'}
                    </Text>
                  </Td>
                </Tr>
              ) : (
                accounts.map((account) => (
                  <Tr key={account.accountId}>
                    <Td borderColor={borderColor}>
                      <Text fontWeight="500">{account.studentName}</Text>
                    </Td>
                    <Td borderColor={borderColor}>
                      <FormattedDocument value={account.studentDocument} />
                    </Td>
                    <Td borderColor={borderColor} isNumeric>
                      <Text
                        fontWeight="bold"
                        color={account.balance > 0 ? 'red.500' : 'green.500'}
                      >
                        {formatCurrency(account.balance)}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor} isNumeric>
                      {formatCurrency(account.totalDebits)}
                    </Td>
                    <Td borderColor={borderColor}>
                      <IconButton
                        icon={<ViewIcon />}
                        size="sm"
                        colorScheme="blue"
                        variant="ghost"
                        onClick={() => handleViewAccount(account)}
                        aria-label="Ver detalle"
                      />
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>

        <Flex
          mt={4}
          justify="space-between"
          align="center"
          gap={4}
          flexWrap="wrap"
        >
          <HStack spacing={3}>
            <Text fontSize="sm" color="gray.500">
              {total === 0
                ? 'Sin resultados'
                : `Mostrando ${from}-${to} de ${total}`}
            </Text>
            <Select
              size="sm"
              maxW="110px"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={10}>10 / pág</option>
              <option value={20}>20 / pág</option>
              <option value={50}>50 / pág</option>
            </Select>
          </HStack>

          <HStack>
            <Button
              size="sm"
              leftIcon={<ChevronLeftIcon />}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              isDisabled={page <= 1 || loading}
            >
              Anterior
            </Button>
            <Text fontSize="sm" minW="90px" textAlign="center">
              Página {page} de {totalPages}
            </Text>
            <Button
              size="sm"
              rightIcon={<ChevronRightIcon />}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              isDisabled={page >= totalPages || loading}
            >
              Siguiente
            </Button>
          </HStack>
        </Flex>
      </Card>

      {selectedAccount && (
        <AccountDetailModal
          isOpen={isOpen}
          onClose={onClose}
          account={selectedAccount}
          onUpdate={loadAccounts}
        />
      )}
    </Box>
  );
}
